import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Clock, AlertCircle } from 'lucide-react';

/**
 * Rate Limit Modal - Shows when user exceeds API rate limits
 * Displays friendly message with countdown timer
 */
const RateLimitModal = ({ isOpen, onClose, resetTime, limitType = 'minute' }) => {
  const [timeRemaining, setTimeRemaining] = useState(0);

  // Calculate time remaining
  useEffect(() => {
    if (!isOpen || !resetTime) return;

    const updateTimer = () => {
      const now = Date.now();
      const remaining = Math.max(0, Math.ceil((resetTime - now) / 1000));
      setTimeRemaining(remaining);

      // Auto-close when timer expires
      if (remaining === 0) {
        setTimeout(() => {
          onClose();
        }, 1000);
      }
    };

    // Update immediately
    updateTimer();

    // Update every second
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [isOpen, resetTime, onClose]);

  // Format time remaining
  const formatTime = (seconds) => {
    if (seconds < 60) {
      return `${seconds} second${seconds !== 1 ? 's' : ''}`;
    }
    const minutes = Math.ceil(seconds / 60);
    return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
  };

  // Get friendly message based on limit type
  const getMessage = () => {
    switch (limitType) {
      case 'minute':
        return {
          title: "Taking It Slow",
          description: "You're exploring God's Word quite eagerly! To ensure the best experience for everyone, please take a brief pause.",
          icon: "🕊️"
        };
      case 'hour':
        return {
          title: "Time for Reflection",
          description: "You've been studying deeply! Let's take a moment to reflect on what you've learned before continuing.",
          icon: "📖"
        };
      case 'day':
        return {
          title: "Daily Limit Reached",
          description: "You've reached your daily exploration limit. Come back tomorrow to continue your spiritual journey!",
          icon: "✨"
        };
      default:
        return {
          title: "Please Wait",
          description: "You've reached your usage limit. Please wait a moment before continuing.",
          icon: "⏱️"
        };
    }
  };

  const message = getMessage();

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-60">
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden"
        >
          {/* Header with gradient */}
          <div className="bg-gradient-to-r from-blue-500 to-purple-600 p-6 text-white text-center">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
              className="text-5xl mb-3"
            >
              {message.icon}
            </motion.div>
            <h2 className="text-2xl font-bold mb-2">
              {message.title}
            </h2>
          </div>

          {/* Content */}
          <div className="p-6">
            <p className="text-gray-600 text-center mb-6 leading-relaxed">
              {message.description}
            </p>

            {/* Timer Display */}
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-6 mb-6">
              <div className="flex items-center justify-center gap-3 mb-3">
                <Clock className="text-purple-600" size={24} />
                <span className="text-gray-600 font-medium">Time until reset</span>
              </div>
              <motion.div
                key={timeRemaining}
                initial={{ scale: 1.1 }}
                animate={{ scale: 1 }}
                className="text-center"
              >
                <div className="text-4xl font-bold text-purple-600">
                  {formatTime(timeRemaining)}
                </div>
              </motion.div>
            </div>

            {/* Info Box */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={20} />
                <div className="text-sm text-gray-700">
                  <p className="font-semibold mb-1">Why do we have limits?</p>
                  <p className="text-gray-600">
                    Rate limits help us provide a smooth experience for all users and keep our
                    Bible study app sustainable for everyone.
                  </p>
                </div>
              </div>
            </div>

            {/* Suggestions */}
            <div className="bg-purple-50 rounded-lg p-4 mb-6">
              <p className="text-sm font-semibold text-purple-900 mb-2">
                While you wait, you can:
              </p>
              <ul className="text-sm text-purple-800 space-y-1">
                <li>• Review and reflect on previous responses</li>
                <li>• Take notes on what you've learned</li>
                <li>• Pray about the insights you've gained</li>
              </ul>
            </div>

            {/* Close Button */}
            <button
              onClick={onClose}
              className="w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white font-semibold py-3 px-6 rounded-full hover:shadow-lg transition-all"
            >
              Understood
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default RateLimitModal;
